/**
 * Format a number as British Pounds with appropriate scaling
 * @param value - The numeric value to format
 * @param compact - Whether to use compact notation (K, M)
 * @returns Formatted string with £ symbol
 */
export function formatCurrency(value: number, compact: boolean = true): string {
  if (compact) {
    if (value >= 1000000) {
      return `£${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `£${(value / 1000).toFixed(1)}K`;
    }
  }
  return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * Calculate nice Y-axis tick values based on data range
 * @param minValue - Minimum value in dataset
 * @param maxValue - Maximum value in dataset
 * @param tickCount - Desired number of ticks (default: 5)
 * @returns Array of tick values
 */
export function calculateYAxisTicks(minValue: number, maxValue: number, tickCount: number = 5): number[] {
  const range = maxValue - minValue;
  const roughStep = range / (tickCount - 1);
  
  // Find a nice step size (1, 2, 5, 10, 20, 50, 100, etc.)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  
  let niceStep: number;
  if (residual <= 1) {
    niceStep = magnitude;
  } else if (residual <= 2) {
    niceStep = 2 * magnitude;
  } else if (residual <= 5) {
    niceStep = 5 * magnitude;
  } else {
    niceStep = 10 * magnitude;
  }
  
  // Calculate nice min and max
  const niceMin = Math.floor(minValue / niceStep) * niceStep;
  const niceMax = Math.ceil(maxValue / niceStep) * niceStep;
  
  // Generate ticks
  const ticks: number[] = [];
  for (let tick = niceMin; tick <= niceMax; tick += niceStep) {
    ticks.push(tick);
  }
  
  return ticks;
}

/**
 * Normalize a value to SVG coordinates
 * @param value - The value to normalize
 * @param minValue - Minimum value in range
 * @param maxValue - Maximum value in range
 * @param height - SVG height
 * @param padding - Padding from edges
 * @returns Y coordinate in SVG space
 */
export function normalizeToSVG(
  value: number,
  minValue: number,
  maxValue: number,
  height: number,
  padding: number = 20
): number {
  const range = maxValue - minValue;
  const normalizedValue = (value - minValue) / range;
  return height - padding - normalizedValue * (height - 2 * padding);
}
